/**
 * DevPlanSwitcher
 * 
 * 개발/스테이징 환경에서 플랜을 변경하며 테스트할 수 있는 컴포넌트
 * Production 환경에서는 렌더링되지 않음
 */

import { useEffect } from 'react';
import { APP_ENV } from '../api';
import { useAuth } from '../auth';

type PlanType = 'free' | 'pro' | 'max' | 'team';

const PLANS: PlanType[] = ['free', 'pro', 'max', 'team'];
const DEV_PLAN_OVERRIDE_KEY = 'releasegate_dev_plan_override';
const DEV_PLAN_PREVIOUS_KEY = 'releasegate_dev_plan_previous';

const PLAN_LABELS: Record<PlanType, string> = {
  free: 'Free',
  pro: 'Pro',
  max: 'Max',
  team: 'Team',
};

const PLAN_COLORS: Record<PlanType, { active: string; inactive: string }> = {
  free: {
    active: 'bg-white text-black border-white',
    inactive: 'bg-transparent text-white/50 border-white/20 hover:border-white/40 hover:text-white/70',
  },
  pro: {
    active: 'bg-[var(--neo-mint)] text-black border-[var(--neo-mint)]',
    inactive: 'bg-transparent text-[var(--neo-mint)]/50 border-[var(--neo-mint)]/20 hover:border-[var(--neo-mint)]/40 hover:text-[var(--neo-mint)]/70',
  },
  max: {
    active: 'bg-[var(--neo-pink)] text-black border-[var(--neo-pink)]',
    inactive: 'bg-transparent text-[var(--neo-pink)]/50 border-[var(--neo-pink)]/20 hover:border-[var(--neo-pink)]/40 hover:text-[var(--neo-pink)]/70',
  },
  team: {
    active: 'bg-[var(--neo-yellow)] text-black border-[var(--neo-yellow)]',
    inactive: 'bg-transparent text-[var(--neo-yellow)]/50 border-[var(--neo-yellow)]/20 hover:border-[var(--neo-yellow)]/40 hover:text-[var(--neo-yellow)]/70',
  },
};

function isDevEnvironment(): boolean {
  const env = (APP_ENV || 'local').toLowerCase();
  return env !== 'production';
}

export default function DevPlanSwitcher() {
  const { quota } = useAuth();
  
  // localStorage에서 직접 읽어옴 (새로고침 후에도 유지)
  const storedOverride = (() => {
    try {
      const stored = localStorage.getItem(DEV_PLAN_OVERRIDE_KEY);
      if (stored && ['free', 'pro', 'max', 'team'].includes(stored)) {
        return stored as PlanType;
      }
    } catch {
      // ignore
    }
    return null;
  })();
  
  const actualPlan = quota?.actual_plan || quota?.plan || 'free';
  const currentPlan = storedOverride || actualPlan;
  
  // 새로고침 후 전/후 비교 로그 출력 (Hook은 조건부 return 전에 호출)
  useEffect(() => {
    // 개발 환경이 아니거나 quota가 없으면 스킵
    if (!isDevEnvironment() || !quota) return;
    
    try {
      const previousPlan = localStorage.getItem(DEV_PLAN_PREVIOUS_KEY);
      const currentOverride = localStorage.getItem(DEV_PLAN_OVERRIDE_KEY);
      
      if (previousPlan) {
        console.log('%c[DevPlanSwitcher] 플랜 변경 확인', 'color: #00ff88; font-weight: bold;');
        console.log(`  📋 변경 전: ${previousPlan}`);
        console.log(`  📋 변경 후: ${currentOverride || actualPlan}`);
        console.log(`  📋 실제 플랜: ${actualPlan}`);
        console.log(`  📋 오버라이드: ${currentOverride || '없음'}`);
        console.log(`  ✅ 적용된 플랜: ${currentPlan}`);
        
        // 비교 결과
        const expected = currentOverride || actualPlan;
        if (previousPlan !== expected) {
          console.log('%c  ✅ 플랜이 정상적으로 변경되었습니다!', 'color: #00ff88;');
        } else {
          console.log('%c  ⚠️ 플랜이 변경되지 않았습니다.', 'color: #ffaa00;');
        }
        
        // 비교용 데이터 삭제 (한 번만 로그)
        localStorage.removeItem(DEV_PLAN_PREVIOUS_KEY);
      } else {
        // 일반 로드 시 현재 상태 로그
        console.log('%c[DevPlanSwitcher] 현재 플랜 상태', 'color: #00aaff; font-weight: bold;');
        console.log(`  📋 실제 플랜: ${actualPlan}`);
        console.log(`  📋 오버라이드: ${currentOverride || '없음'}`);
        console.log(`  ✅ 적용된 플랜: ${currentPlan}`);
      }
    } catch (e) {
      console.error('[DevPlanSwitcher] 로그 출력 오류:', e);
    }
  }, [actualPlan, currentPlan, quota]);
  
  // Production 환경에서는 렌더링하지 않음
  if (!isDevEnvironment()) return null;
  
  // 로그인 상태에서만 표시
  if (!quota) return null;
  
  const handlePlanChange = (plan: PlanType) => {
    if (plan === currentPlan) return;
    
    try {
      // 변경 전 플랜 저장 (새로고침 후 비교용)
      localStorage.setItem(DEV_PLAN_PREVIOUS_KEY, currentPlan);
      
      // 새 플랜 저장
      localStorage.setItem(DEV_PLAN_OVERRIDE_KEY, plan);
      
      console.log('%c[DevPlanSwitcher] 플랜 변경 요청', 'color: #ffaa00; font-weight: bold;');
      console.log(`  📋 현재: ${currentPlan} → 변경: ${plan}`);
      console.log('  🔄 새로고침 중...');
      
      // 페이지 새로고침
      window.location.reload();
    } catch (e) {
      console.error('[DevPlanSwitcher] 플랜 변경 오류:', e);
    }
  };
  
  const handleReset = () => {
    try {
      // 변경 전 플랜 저장
      localStorage.setItem(DEV_PLAN_PREVIOUS_KEY, currentPlan);
      
      // 오버라이드 삭제
      localStorage.removeItem(DEV_PLAN_OVERRIDE_KEY);
      
      console.log('%c[DevPlanSwitcher] 플랜 리셋 요청', 'color: #ffaa00; font-weight: bold;');
      console.log(`  📋 현재: ${currentPlan} → 리셋: ${actualPlan}`);
      console.log('  🔄 새로고침 중...');
      
      // 페이지 새로고침
      window.location.reload();
    } catch (e) {
      console.error('[DevPlanSwitcher] 플랜 리셋 오류:', e);
    }
  };
  
  return (
    <div 
      className="fixed left-0 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-1 p-1.5 bg-black/80 backdrop-blur-sm border-r border-t border-b border-white/10 rounded-r-lg shadow-xl"
      data-testid="dev-plan-switcher"
    >
      {/* 헤더 */}
      <div className="text-[8px] font-bold text-white/40 uppercase tracking-wider text-center mb-1 px-1">
        DEV
      </div>
      
      {/* 플랜 버튼들 */}
      {PLANS.map((plan) => {
        const isActive = plan === currentPlan;
        const colors = PLAN_COLORS[plan];
        
        return (
          <button
            key={plan}
            onClick={() => handlePlanChange(plan)}
            disabled={isActive}
            className={`
              px-2 py-1.5 text-[10px] font-bold border rounded transition-all duration-200
              ${isActive ? colors.active : colors.inactive}
              ${isActive ? 'cursor-default' : 'cursor-pointer'}
            `}
            title={isActive ? `현재 플랜: ${PLAN_LABELS[plan]}` : `${PLAN_LABELS[plan]} 플랜으로 전환`}
          >
            {PLAN_LABELS[plan]}
          </button>
        );
      })}
      
      {/* 리셋 버튼 (오버라이드 중일 때만 표시) */}
      {storedOverride && (
        <button
          onClick={handleReset}
          className="mt-1 px-2 py-1 text-[8px] font-bold text-white/40 hover:text-white/70 border border-white/10 hover:border-white/30 rounded transition-all duration-200"
          title="원래 플랜으로 복구"
        >
          Reset
        </button>
      )}
    </div>
  );
}

